/* eslint-disable no-confusing-arrow */
import {
  BudgetDailyItemData,
  ExpensesClassificationType,
  salesPlanDailyDateType,
} from 'domain/master/storeManagement/salesBudgetDailyInput';
import { BudgetMonthlyItemData } from 'domain/master/storeManagement/salesBudgetYearInput';

export const formatterPercent = new Intl.NumberFormat('ja-JP', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  style: 'percent',
});

export const showExpensesClassificationText = (expensesClassification: number | null) => {
  if (expensesClassification === null) return '';

  return expensesClassification === ExpensesClassificationType.VARIABLE_COST ? '変' : '固';
};

export const getWeekDayStyle = (weekday: string) => {
  if (weekday === '土') return 'saturday text-center active';

  if (weekday === '日' || weekday === '祝') {
    return 'text-center active red-day';
  }

  return 'text-center active';
};

export const getMonthlyTotalCostList = (data: BudgetDailyItemData[], dateData: salesPlanDailyDateType[]) => {
  const calcValues = dateData.map((_, index) => {
    let totalCost = 0;
    let totalVariableCost = 0;
    let totalFixedCost = 0;

    data.forEach((item) => {
      // 集計フラグが0（対象外）の場合は集計対象に含めない
      if (item.sumFlag === 0) {
        return;
      }

      const value = item.dayAryList[index] ?? 0;

      const calcValue = calcCost(value, item.expensesClassification);

      totalCost += calcValue.cost;
      totalVariableCost += calcValue.variableCost;
      totalFixedCost += calcValue.fixedCost;
    });
    return { totalCost, totalVariableCost, totalFixedCost };
  });

  return calcValues;
};

export const getYearlyTotalCostList = (data: BudgetMonthlyItemData[], dateData: string[]) => {
  const calcValues = dateData.map((_, index) => {
    let totalCost = 0;
    let totalVariableCost = 0;
    let totalFixedCost = 0;

    data.forEach((item) => {
      // 集計フラグが0（対象外）の場合は集計対象に含めない
      if (item.sumFlag === 0) {
        return;
      }

      const value = item.monthAry[index] ?? 0;

      const calcValue = calcCost(value, item.expensesClassification);

      totalCost += calcValue.cost;
      totalVariableCost += calcValue.variableCost;
      totalFixedCost += calcValue.fixedCost;
    });
    return { totalCost, totalVariableCost, totalFixedCost };
  });

  return calcValues;
};

const calcCost = (value: number, expensesClassification: ExpensesClassificationType) => {
  const variableCost = expensesClassification === ExpensesClassificationType.VARIABLE_COST ? value : 0;
  const fixedCost = expensesClassification === ExpensesClassificationType.FIXED_COST ? value : 0;

  return { cost: value, variableCost, fixedCost };
};

// その他計算
export const calcOtherCostRate = (foodAndDrinkCost: number, otherCost: number) =>
  foodAndDrinkCost > 0 ? otherCost / foodAndDrinkCost : 0;

// 客数計算
export const calcNumberOfCustomers = (perCustomerPrice: number, totalCost: number) =>
  perCustomerPrice > 0 ? Math.round(totalCost / perCustomerPrice) : 0;

// 粗利益計算
export const calcGrossProfit = (sales: number, totalCost: number) => sales - totalCost;

// 回転率計算
export const calcTurnoverRate = (numberOfSeat: number, numberOfCustomers: number) =>
  numberOfSeat > 0 ? numberOfCustomers / numberOfSeat / 100 : 0;

// 限界利益額計算
export const calcMarginalProfit = (totalCost: number, variableCost: number) => totalCost - variableCost;

// 限界利益率計算
export const calcMarginalProfitRate = (totalCost: number, marginalProfit: number) =>
  totalCost > 0 ? marginalProfit / totalCost : 0;

// 貢献利益額計算
export const calcContributionMargin = (marginalProfit: number, fixedCost: number) => marginalProfit - fixedCost;

// 貢献利益率計算
export const calcContributionMarginRate = (totalCost: number, contributionMargin: number) =>
  totalCost > 0 ? contributionMargin / totalCost : 0;

// 売上損益分岐点計算
export const calcBreakEventPointSale = (marginalProfitRate: number, fixedCost: number) =>
  marginalProfitRate > 0 ? Math.round(fixedCost / marginalProfitRate) : 0;

// 客数損益分岐点計算
export const calcBreakEventPointCustomer = (perCustomerPrice: number, breakEventPointSale: number) =>
  perCustomerPrice > 0 ? Math.round(breakEventPointSale / perCustomerPrice) : 0;

// 人件費率合計計算
export const calcEmployeeAndPartCostRate = (totalCost: number, laborCost: number) =>
  totalCost > 0 ? laborCost / totalCost : 0;

// 人件費率計算
export const calcLaborRate = (grossProfit: number, totalCost: number) =>
  grossProfit > 0 ? totalCost / grossProfit : 0;

// FLA合計計算
export const calcFLACost = (foodAndDrinkCost: number, laborCost: number, advertisingCost: number) =>
  foodAndDrinkCost + laborCost + advertisingCost;

// FLA率計算
export const calcFLACostRate = (totalCost: number, FLACost: number) => (totalCost > 0 ? FLACost / totalCost : 0);

// 店舗損益額計算
export const calcProfitLossCost = (totalCost: number, FLACost: number) => totalCost - FLACost;

// 店舗損益率計算
export const calcProfitLossCostRate = (totalCost: number, profitLossCost: number) =>
  totalCost > 0 ? profitLossCost / totalCost : 0;

// FLARO合計計算
export const calcFLAROCost = (FLACost: number, rentCost: number, otherCost: number) => FLACost + rentCost + otherCost;

// FLARO率計算
export const calcFLAROCostRate = (totalCost: number, FLAROCost: number) => (totalCost > 0 ? FLAROCost / totalCost : 0);

// 営業利益計算
export const calcOperatingIncome = (totalCost: number, FLAROCost: number) => totalCost - FLAROCost;

// 営業利益率計算
export const calcOperatingIncomeRate = (totalCost: number, operatingIncome: number) =>
  totalCost > 0 ? operatingIncome / totalCost : 0;
